"use client"

import React, { useRef, useEffect, useState } from "react";
import MediaBoxes from "../../components/MediaBoxes/MediaBoxes";
import PropTypes from 'prop-types';
import { formatString } from "@/lib/formatString";
import dynamic from 'next/dynamic';

// ✅ Dynamically import Lottie with SSR disabled
const Lottie = dynamic(() => import("lottie-react"), { ssr: false });
import blogLottie from "../../assets/bloglottie.json";

function MediaPage({ blok }) {
    const [displayedItems, setDisplayedItems] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const loadingRef = useRef(null);
    const itemsPerPage = 6;

    // Initialize displayed items
    useEffect(() => {
        if (blok.Media && blok.Media.length > 0) {
            const initialItems = blok.Media.slice(0, itemsPerPage);
            setDisplayedItems(initialItems);
            setCurrentIndex(itemsPerPage);
            setHasMore(itemsPerPage < blok.Media.length);
        }
    }, [blok.Media]);

    // Load more items function
    const loadMoreItems = () => {
        if (!hasMore || loading) return;
        
        setLoading(true);
        
        // Simulate loading delay
        setTimeout(() => {
            const nextItems = blok.Media.slice(currentIndex, currentIndex + itemsPerPage);
            setDisplayedItems(prev => [...prev, ...nextItems]);
            setCurrentIndex(prev => prev + itemsPerPage);
            setHasMore(currentIndex + itemsPerPage < blok.Media.length);
            setLoading(false);
        }, 500);
    };

    // Intersection Observer for infinite scroll
    useEffect(() => {
        if (!hasMore || loading) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMoreItems();
                }
            },
            { root: null, rootMargin: '100px', threshold: 0 }
        );
        
        if (loadingRef.current) observer.observe(loadingRef.current);
        return () => observer.disconnect();
    }, [hasMore, loading, currentIndex]);

    return (
        <div className="pb-20 overflow-x-hidden w-full">
            <div
                className="bg-asmPageTopBG bg-repeat-y"
                style={{
                    backgroundSize: "cover",
                    backgroundPosition: "bottom",
                    backgroundRepeat: "repeat",
                }}
            >
                <div className="max-w-[1920px] m-auto">
                    <div className="relative lg:min-h-[1000px] lg:flex flex-col items-center py-8 max-w-[1920px] m-auto">
                        {/* Header Section */}
                        <div className="font-aileron py-20 lg:py-32 text-center font-bold">
                            <h1 className="bg-custom-radial bg-clip-text text-transparent text-[48px] lg:text-[80px] px-4">
                                {formatString(blok.Title)}
                            </h1>
                            {blok?.Subtitle && (
                                <div className="font-lato mt-4 text-[20px] w-[80%] lg:w-[90%] m-auto lg:text-[32px] font-normal">
                                    {formatString(blok.Subtitle)}
                                </div>
                            )}
                        </div>

                        {/* Media Items Container */}
                        <div className="lg:w-[1500px] lg:p-0 p-4 space-y-[40px] -mt-20">
                            {displayedItems.map((media, index) => (
                                <MediaBoxes
                                    key={index}
                                    title={media.title}
                                    subtitle={media.subtitle}
                                    description={media.description}
                                    buttonText={media.buttonText}
                                    url={media.url.url}
                                    index={index}
                                />
                            ))}

                            {/* Loading animation */}
                            {loading && (
                                <div className="flex justify-center items-center py-8">
                                    <Lottie animationData={blogLottie} className='w-48 h-48' />
                                </div>
                            )}

                            {/* Intersection Observer target */}
                            {hasMore && !loading && (
                                <div ref={loadingRef} className="h-10"></div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

MediaPage.propTypes = {
    blok: PropTypes.shape({
        Title: PropTypes.string,
        Subtitle: PropTypes.string,
        Media: PropTypes.array
    }).isRequired
};

export default MediaPage;

